import { TableColumnMeta } from "ds/components/Table/types";
import { SearchQueryOrderDirection } from "types/generated";
import { MAX_CELL_WIDTH, STICKY_ACTION_COLUMN_MAX_WIDTH } from "ds/components/Table/constants";

import { IAC_MANAGEMENTS_TABLE_COLUMNS } from "./types";
import { ResourcesSuggestions } from "../useIacManagementFilters/constants";

export const PAGINATION_OPTIONS = [10, 25, 50];
export const RESOURCES_ROWS_PER_PAGE = 50;
export const GROUPS_LIMIT = 50;
export const GROUPS_LIMIT_STORAGE_KEY = "groupsLimit";
export const ADD_TO_FILTERS = "Add to filters";

export const TABLE_COLUMNS: Record<TableColumnMeta["id"], TableColumnMeta> = {
  [IAC_MANAGEMENTS_TABLE_COLUMNS.NAME]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.NAME,
    title: "Name",
    minWidth: 170,
    defaultWidth: 350,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCES]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCES,
    title: "Resources",
    minWidth: 150,
    defaultWidth: 200,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canMove: true,
    canPin: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCE_TYPE]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCE_TYPE,
    title: "Resource type",
    minWidth: 170,
    defaultWidth: 200,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCE_ADDRESS]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCE_ADDRESS,
    title: "Resource address",
    minWidth: 190,
    defaultWidth: 220,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.DRIFT_DETECTED]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.DRIFT_DETECTED,
    title: "Drift detected",
    minWidth: 170,
    defaultWidth: 200,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.SPACE]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.SPACE,
    title: "Space",
    minWidth: 130,
    defaultWidth: 250,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.VENDOR]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.VENDOR,
    title: "Vendor",
    minWidth: 140,
    defaultWidth: 250,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.UPDATED_AT]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.UPDATED_AT,
    title: "Updated at",
    minWidth: 160,
    defaultWidth: 200,
    maxWidth: MAX_CELL_WIDTH,
    allowsSorting: true,
    resizable: true,
    canHide: true,
    canPin: true,
    canMove: true,
  },
  [IAC_MANAGEMENTS_TABLE_COLUMNS.ACTIONS]: {
    id: IAC_MANAGEMENTS_TABLE_COLUMNS.ACTIONS,
    title: "Row actions",
    noHeader: true,
    minWidth: STICKY_ACTION_COLUMN_MAX_WIDTH,
    defaultWidth: STICKY_ACTION_COLUMN_MAX_WIDTH,
    maxWidth: STICKY_ACTION_COLUMN_MAX_WIDTH,
    allowsSorting: false,
    resizable: false,
    canHide: false,
    canPin: false,
    configurable: false,
    canMove: false,
  },
};

export const initialSortOption = IAC_MANAGEMENTS_TABLE_COLUMNS.NAME;
export const initialSortDirection = SearchQueryOrderDirection.Desc;
export const FILTERS_TYPE = "resources";
export const FILTERS_ORDER_SETTINGS_KEY = "iacManagementFiltersOrder";

export const SORT_OPTIONS_STACK_GROUP: Record<TableColumnMeta["id"], string> = {
  [IAC_MANAGEMENTS_TABLE_COLUMNS.NAME]: ResourcesSuggestions.EntityId,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCES]: ResourcesSuggestions.ResourcesCount,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.DRIFT_DETECTED]: ResourcesSuggestions.Drifted,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.SPACE]: ResourcesSuggestions.Space,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.VENDOR]: ResourcesSuggestions.Vendor,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.UPDATED_AT]: ResourcesSuggestions.UpdatedAt,
};

export const SORT_OPTIONS_PROVIDER_GROUP: Record<TableColumnMeta["id"], string | undefined> = {
  [IAC_MANAGEMENTS_TABLE_COLUMNS.NAME]: ResourcesSuggestions.EntityId,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCES]: ResourcesSuggestions.ResourcesCount,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.DRIFT_DETECTED]: ResourcesSuggestions.Drifted,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.SPACE]: undefined,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.VENDOR]: undefined,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.UPDATED_AT]: ResourcesSuggestions.UpdatedAt,
};

export const SORT_OPTIONS_RESOURCE_TYPE_GROUP: Record<TableColumnMeta["id"], string | undefined> = {
  [IAC_MANAGEMENTS_TABLE_COLUMNS.NAME]: ResourcesSuggestions.EntityId,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCES]: ResourcesSuggestions.ResourcesCount,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.DRIFT_DETECTED]: ResourcesSuggestions.Drifted,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.SPACE]: undefined,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.VENDOR]: undefined,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.UPDATED_AT]: ResourcesSuggestions.UpdatedAt,
};

export const SORT_OPTIONS_RESOURCES: Record<TableColumnMeta["id"], string | undefined> = {
  [IAC_MANAGEMENTS_TABLE_COLUMNS.NAME]: ResourcesSuggestions.EntityId,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCE_ADDRESS]: ResourcesSuggestions.ResourceAddress,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCE_TYPE]: ResourcesSuggestions.ResourceType,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.RESOURCES]: undefined,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.DRIFT_DETECTED]: ResourcesSuggestions.Drifted,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.SPACE]: ResourcesSuggestions.Space,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.VENDOR]: ResourcesSuggestions.Vendor,
  [IAC_MANAGEMENTS_TABLE_COLUMNS.UPDATED_AT]: ResourcesSuggestions.UpdatedAt,
};
